import { useEffect, useRef, useState } from 'react'

export function useDebouncedSearch<T>(
    query: string,
    searchFn: (query: string) => Promise<T[]>,
    delay: number = 300
) {
    const [results, setResults] = useState<T[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const timeoutRef = useRef<NodeJS.Timeout>()

    useEffect(() => {
        if (!query) {
            setResults([])
            // Clearing the box after a search had already started otherwise
            // left the spinner running with nothing in flight.
            setLoading(false)
            return
        }

        setLoading(true)
        clearTimeout(timeoutRef.current)

        timeoutRef.current = setTimeout(async () => {
            try {
                const res = await searchFn(query)
                setResults(res)
                setError(null)
            } catch (err: unknown) {
                console.error(err)
                setError('Search failed')
                setResults([])
            } finally {
                setLoading(false)
            }
        }, delay)

        return () => clearTimeout(timeoutRef.current)
    }, [query, delay, searchFn])

    return { results, loading, error }
}
